import { HeaderIcons } from '../shared/icons';
import React, { useState, useEffect, useRef, useCallback } from 'react';
import { WebSocketAdapter } from '../../utils/WebSocketAdapter';
import Header from '../shared/Header';
import { WaitingSpinner, GameEndedScreen, LoadingQuestion, TimerBar } from '../shared';
import { useGameEndScreenData } from '../../hooks/useGameEndScreenData';
import { getGameName } from '../../utils/gameUtils';
import type { GameStatistics } from '../../types/game';
import { CatchShoutCatchGatherer, CatchShoutCatchWhisperer } from './catchShoutCatch';
import '../../App.css';
import '../../styles/game-layout.css';
import '../../styles/player-screen.css';
import { useTranslation } from '../../i18n';

type CatchShoutCatchRole = 'whisperer' | 'gatherer';

interface CatchShoutCatchQuestion {
  questionId: string;
  timeLimit: number;
  startTime: number;
  playerId: string;
  role: CatchShoutCatchRole;
  teamId: 'A' | 'B';
  themeTitle: string;
  animals: string[];
  targetEmoji?: string;
  whispererName?: string;
  roundCatches?: { A: number; B: number };
  questionNumber?: number;
  totalQuestions?: number;
}

interface CatchShoutCatchPlayerDisplayProps {
  roomState: {
    roomName: string;
    gameType: string;
    hostId: string;
    players: Array<{ id: string; name: string; score: number; avatar?: string; teamId?: 'A' | 'B' }>;
    teams?: { A: { score: number; name: string }; B: { score: number; name: string } };
    gameState: 'waiting' | 'playing' | 'finished';
    currentQuestion?: {
      questionId?: string;
      id?: string;
      question?: string;
      title?: string;
      timeLimit?: number;
      startTime?: number;
      questionNumber?: number;
      totalQuestions?: number;
      isCatchShoutCatch?: boolean;
      themeTitle?: string;
      animals?: string[];
    };
  };
  socket: WebSocketAdapter;
  myPlayerId: string;
  onGoHome?: () => void;
  onLoadingTimeout?: () => void;
  isHost?: boolean;
  onBackToGameSelection?: () => void;
}

const CatchShoutCatchPlayerDisplay: React.FC<CatchShoutCatchPlayerDisplayProps> = ({
  roomState,
  socket,
  myPlayerId,
  onGoHome,
  onLoadingTimeout,
  isHost = false,
  onBackToGameSelection,
}) => {
  const { t } = useTranslation();
  const [cscQuestion, setCscQuestion] = useState<CatchShoutCatchQuestion | null>(null);
  const [timeRemaining, setTimeRemaining] = useState(0);
  const [gameEnded, setGameEnded] = useState(false);
  const [finalLeaderboard, setFinalLeaderboard] = useState<Array<{ playerId: string; playerName: string; score: number }>>([]);
  const [myScore, setMyScore] = useState(0);
  const [gameStatistics, setGameStatistics] = useState<GameStatistics | null>(null);
  const [targetEmoji, setTargetEmoji] = useState<string | undefined>();
  const [targetChanged, setTargetChanged] = useState(false);
  const [roundCatches, setRoundCatches] = useState<{ A: number; B: number }>({ A: 0, B: 0 });
  const currentQuestionIdRef = useRef<string | undefined>(undefined);
  const myTeamIdRef = useRef<'A' | 'B' | undefined>(undefined);
  const gameEndData = useGameEndScreenData(roomState);

  useEffect(() => {
    if (myPlayerId && roomState.players) {
      const me = roomState.players.find((p) => p.id === myPlayerId);
      if (me) {
        setMyScore(me.score);
        if (me.teamId) myTeamIdRef.current = me.teamId;
      }
    }
  }, [roomState.players, myPlayerId]);

  useEffect(() => {
    if (roomState.gameState === 'finished' && gameEndData) {
      setGameEnded(true);
      setCscQuestion(null);
      setFinalLeaderboard(gameEndData.finalLeaderboard);
      setGameStatistics(gameEndData.gameStatistics);
    } else if (roomState.gameState === 'playing' && gameEnded) {
      setGameEnded(false);
      setCscQuestion(null);
      setFinalLeaderboard([]);
      setGameStatistics(null);
      currentQuestionIdRef.current = undefined;
      setTargetEmoji(undefined);
      setRoundCatches({ A: 0, B: 0 });
    }
  }, [roomState.gameState, gameEndData, gameEnded]);

  useEffect(() => {
    if (!cscQuestion && roomState.currentQuestion?.isCatchShoutCatch) {
      const q = roomState.currentQuestion;
      const questionId = q.questionId || q.id;
      if (questionId && socket && roomState.roomName) {
        socket.emit('get-catch-shout-catch-state', { roomName: roomState.roomName, questionId });
      }
    }
  }, [roomState.currentQuestion, cscQuestion, socket, roomState.roomName]);

  useEffect(() => {
    const handleQuestion = (data: CatchShoutCatchQuestion) => {
      const actualPlayerId = myPlayerId || socket.id;
      if (data.playerId && data.playerId !== actualPlayerId && data.playerId !== socket.id) {
        return;
      }
      currentQuestionIdRef.current = data.questionId;
      myTeamIdRef.current = data.teamId;
      setCscQuestion(data);
      setTargetEmoji(data.targetEmoji);
      setTargetChanged(false);
      setRoundCatches(data.roundCatches ?? { A: 0, B: 0 });
      setTimeRemaining(data.timeLimit);
    };

    const handleTarget = (data: {
      questionId: string;
      teamId: 'A' | 'B';
      targetEmoji?: string;
      targetChanged?: boolean;
    }) => {
      if (data.questionId !== currentQuestionIdRef.current) return;
      if (data.teamId !== myTeamIdRef.current) return;
      if (data.targetEmoji) setTargetEmoji(data.targetEmoji);
      if (data.targetChanged) setTargetChanged(true);
    };

    const handleCatchResult = (data: {
      questionId: string;
      correct: boolean;
      teamRoundCatches: number;
      teamId: 'A' | 'B';
    }) => {
      if (data.questionId !== currentQuestionIdRef.current) return;
      if (data.teamId !== myTeamIdRef.current) return;
      setRoundCatches((prev) => ({ ...prev, [data.teamId]: data.teamRoundCatches }));
    };

    const handleRoundScore = (data: {
      questionId: string;
      roundCatches: { A: number; B: number };
    }) => {
      if (data.questionId !== currentQuestionIdRef.current) return;
      setRoundCatches(data.roundCatches);
    };

    const handleGameEnded = (data: { leaderboard?: typeof finalLeaderboard; statistics?: GameStatistics }) => {
      setGameEnded(true);
      setCscQuestion(null);
      setFinalLeaderboard(data.leaderboard || []);
      setGameStatistics(data.statistics || null);
    };

    const handleGameStarted = () => {
      setGameEnded(false);
      setCscQuestion(null);
      setFinalLeaderboard([]);
      setGameStatistics(null);
      currentQuestionIdRef.current = undefined;
      setTargetEmoji(undefined);
      setRoundCatches({ A: 0, B: 0 });
    };

    socket.on('catch-shout-catch-question', handleQuestion);
    socket.on('catch-shout-catch-target', handleTarget);
    socket.on('catch-shout-catch-catch-result', handleCatchResult);
    socket.on('catch-shout-catch-round-score', handleRoundScore);
    socket.on('game-ended', handleGameEnded);
    socket.on('game-started', handleGameStarted);

    return () => {
      socket.off('catch-shout-catch-question', handleQuestion);
      socket.off('catch-shout-catch-target', handleTarget);
      socket.off('catch-shout-catch-catch-result', handleCatchResult);
      socket.off('catch-shout-catch-round-score', handleRoundScore);
      socket.off('game-ended', handleGameEnded);
      socket.off('game-started', handleGameStarted);
    };
  }, [socket, myPlayerId]);

  useEffect(() => {
    if (!cscQuestion) return;
    const interval = setInterval(() => {
      const elapsed = Date.now() - cscQuestion.startTime;
      const remaining = Math.max(0, cscQuestion.timeLimit - elapsed);
      setTimeRemaining(remaining);
    }, 100);
    return () => clearInterval(interval);
  }, [cscQuestion]);

  const handleCatch = useCallback(
    (animalEmoji: string, spawnId: string) => {
      if (!cscQuestion || cscQuestion.role !== 'gatherer') return;
      socket.emit('catch-shout-catch-catch', {
        roomName: roomState.roomName,
        questionId: cscQuestion.questionId,
        animalEmoji,
        spawnId,
      });
    },
    [cscQuestion, socket, roomState.roomName]
  );

  if (gameEnded) {
    const handlePlaySameGame = () => {
      setGameEnded(false);
      setCscQuestion(null);
      setFinalLeaderboard([]);
      socket.emit('start-game', { roomName: roomState.roomName, gameType: roomState.gameType });
    };

    const teamLeaderboard = roomState.teams
      ? [
          { playerId: 'A', playerName: roomState.teams.A.name, score: roomState.teams.A.score },
          { playerId: 'B', playerName: roomState.teams.B.name, score: roomState.teams.B.score },
        ].sort((a, b) => b.score - a.score)
      : finalLeaderboard.length > 0
        ? finalLeaderboard
        : (gameEndData?.finalLeaderboard ?? []);

    return (
      <GameEndedScreen
        gameIcon="🎯"
        finalLeaderboard={teamLeaderboard}
        myPlayerId={myPlayerId}
        myTeamId={myTeamIdRef.current ?? roomState.players.find((p) => p.id === myPlayerId)?.teamId}
        myScore={myScore}
        isHost={isHost}
        onGoHome={onGoHome || (() => {})}
        onPlayAgain={handlePlaySameGame}
        onPlayAnotherGame={onBackToGameSelection}
        showPositionHighlight
        statistics={gameStatistics ?? gameEndData?.gameStatistics ?? undefined}
        feedbackConfig={{ roomName: roomState.roomName, gameType: roomState.gameType, socket }}
      />
    );
  }

  if (!cscQuestion) {
    if (roomState.currentQuestion) {
      return (
        <LoadingQuestion
          socket={socket}
          roomName={roomState.roomName}
          title={t('gameplay.loading.question')}
          onGoHome={onGoHome}
          onLongWaitTimeout={onLoadingTimeout}
        />
      );
    }
    return (
      <div className="app">
        <Header
          leftButton={onGoHome ? { icon: HeaderIcons.home, onClick: onGoHome, tooltip: t('common.home') } : undefined}
          roomCode={roomState.roomName}
        />
        <div className="game-player-wrapper">
          <WaitingSpinner message={t('gameplay.catchShoutCatch.waiting')} isLoading />
        </div>
      </div>
    );
  }

  const teamScore = roomState.teams?.[cscQuestion.teamId]?.score ?? 0;
  const roleLabel =
    cscQuestion.role === 'whisperer'
      ? t('gameplay.catchShoutCatch.roleWhisperer')
      : t('gameplay.catchShoutCatch.roleGatherer');
  const gameDisplayName = getGameName(roomState.gameType);
  const roundActive = timeRemaining > 0;

  return (
    <div className="app">
      <Header
        leftButton={{
          icon: HeaderIcons.home,
          onClick: () => onGoHome?.(),
          tooltip: t('gameplay.leaveGame'),
        }}
        roomCode={roomState.roomName}
        rightButton={{
          icon: HeaderIcons.refresh,
          onClick: () => window.location.reload(),
          tooltip: t('common.tooltips.refreshPage'),
        }}
      />

      <div
        className="game-player-wrapper"
        style={
          cscQuestion.role === 'gatherer'
            ? { display: 'flex', flexDirection: 'column', flex: 1, minHeight: 0, height: '100%' }
            : undefined
        }
      >
        {cscQuestion.role === 'whisperer' && (
          <>
            <div className="player-game-info">
              <span className="player-game-name">{gameDisplayName}</span>
              <span className="player-role-badge">{roleLabel}</span>
            </div>
            <TimerBar
              timeRemaining={timeRemaining}
              timeLimit={cscQuestion.timeLimit}
              questionNumber={cscQuestion.questionNumber}
              totalQuestions={cscQuestion.totalQuestions}
              showTimeCounter={false}
            />
            <div
              style={{
                textAlign: 'center',
                fontWeight: 700,
                fontSize: '1rem',
                color: 'var(--text-primary)',
                margin: '0.5rem 0',
              }}
            >
              {t('gameplay.catchShoutCatch.teamScore', { team: cscQuestion.teamId, score: teamScore })}
            </div>
            <CatchShoutCatchWhisperer
              themeTitle={cscQuestion.themeTitle}
              targetEmoji={targetEmoji}
              targetChanged={targetChanged}
              roundCatches={roundCatches}
              teamId={cscQuestion.teamId}
            />
          </>
        )}

        {cscQuestion.role === 'gatherer' && (
          <CatchShoutCatchGatherer
            animals={cscQuestion.animals}
            timeLimit={cscQuestion.timeLimit}
            startTime={cscQuestion.startTime}
            whispererName={cscQuestion.whispererName}
            disabled={!roundActive}
            onCatch={handleCatch}
          />
        )}
      </div>
    </div>
  );
};

export default CatchShoutCatchPlayerDisplay;
